import { useTranslation } from 'react-i18next';
import { BottomSheet } from './BottomSheet';
import goldMark from '../../assets/brand/incpt-gold.webp';
import { useHaptic } from '../../hooks/useHaptic';
import { daysRunning, periodEarning, roi, type StakePosition } from '../../lib/staking';
import { fitFontSize, fmtUsd, type PositionMeta } from './stakeMeta';

type Props = {
  position: StakePosition | null;
  meta: PositionMeta | null;
  onClose: () => void;
};

const SHARE_GREEN = '#3ddc6b';

/** Hero profit on the share card: four decimals, cents kept. */
const fmtProfit = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`;

/** A dark, shareable result card for one deposit. */
export function StakeShareCard({ position, meta, onClose }: Props) {
  const { t } = useTranslation();
  const { impact } = useHaptic();
  const open = !!position && !!meta;

  const share = () => {
    if (!position || !meta) return;
    impact('light');
    const text = `${meta.name} · ${t('stake.earnedIn', { count: daysRunning(position) })}: +${fmtProfit(
      position.profit,
    )}`;
    const wa = window.Telegram?.WebApp;
    if (wa?.openTelegramLink) {
      wa.openTelegramLink(`https://t.me/share/url?url=${encodeURIComponent(text)}`);
    } else if (navigator.share) {
      void navigator.share({ text }).catch(() => {});
    }
  };

  return (
    <BottomSheet open={open} onClose={onClose}>
      {position && meta && (
        <div className="pb-2">
          <div className="overflow-hidden rounded-[20px] shadow-[0px_10px_30px_rgba(0,0,0,0.28)]">
            <div className="relative overflow-hidden bg-[#08090b] px-[22px] pb-6 pt-5">
              {/* Warm halo + mark — inset from the top edge so it doesn't clip. */}
              <div
                aria-hidden
                className="absolute -right-12 top-3 h-[230px] w-[230px] rounded-full"
                style={{
                  background:
                    'radial-gradient(circle, rgba(206,166,74,.22) 0%, rgba(206,166,74,.06) 46%, rgba(206,166,74,0) 70%)',
                }}
              />
              <img
                aria-hidden
                src={goldMark}
                alt=""
                className="absolute top-5 right-3 h-[168px] w-auto opacity-[0.62] drop-shadow-[0_10px_26px_rgba(0,0,0,0.5)]"
              />

              <div className="relative flex items-center gap-2.5">
                <img src={goldMark} alt="" className="mt-1 block h-6 w-auto" />
                <span className="text-[17px] font-extrabold tracking-[2px] text-white">INCPT</span>
              </div>

              <div className="relative mt-6 flex flex-wrap items-center gap-2.5">
                <span className="text-[21px] font-semibold tracking-[-0.4px] text-white">
                  {meta.name}
                </span>
                <span
                  className="rounded-[20px] border px-2.5 py-[3px] text-[12.5px] font-medium"
                  style={{ color: SHARE_GREEN, borderColor: 'rgba(61,220,107,.4)' }}
                >
                  {meta.rate}
                </span>
              </div>

              <div className="relative mt-4 text-[13px] text-[#8a8f99]">
                {t('stake.earnedIn', { count: daysRunning(position) })}
              </div>
              <div
                className="relative z-10 mt-0.5 whitespace-nowrap font-semibold leading-[1.05] tracking-[-2.2px]"
                style={{ color: SHARE_GREEN, fontSize: fitFontSize(`+${fmtProfit(position.profit)}`, 46, 11) }}
              >
                +{fmtProfit(position.profit)}
              </div>
              <div className="relative z-10 mt-1.5 text-[13px] text-[#8a8f99]">
                {meta.accrual === 'daily' ? t('stake.forecastToday') : t('stake.forecastMonth')}{' '}
                <span style={{ color: SHARE_GREEN }}>
                  +{fmtUsd(periodEarning(position, meta.dailyRate, meta.accrual))}
                </span>
              </div>

              {/* Three columns, as on the prototype's card. */}
              <div className="relative z-10 mt-5 flex flex-wrap gap-x-5 gap-y-3">
                <ShareStat label={t('stake.deposited')} value={fmtUsd(position.invested)} />
                <ShareStat label={t('stake.shareNow')} value={fmtUsd(position.current)} />
                <ShareStat
                  label={t('stake.shareRoi')}
                  value={`+${roi(position).toFixed(2)}%`}
                  color={SHARE_GREEN}
                />
              </div>
            </div>
          </div>

          <div className="mt-3.5 flex gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="h-12 flex-1 rounded-[15px] bg-black/[0.05] text-[15px] font-medium text-ink active:bg-black/[0.1]"
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={share}
              className="h-12 flex-1 rounded-[15px] bg-ink text-[15px] font-semibold text-white active:opacity-90"
            >
              {t('stake.share')}
            </button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}

function ShareStat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div>
      <div className="text-[12.5px] text-[#8a8f99]">{label}</div>
      <div
        className="mt-0.5 whitespace-nowrap font-medium"
        style={{ color: color ?? '#fff', fontSize: fitFontSize(value, 19, 9) }}
      >
        {value}
      </div>
    </div>
  );
}
